import React, { useState, useEffect } from 'react';
import { spotService } from '../../services/spotService';
import { storageService } from '../../services/storageService';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const SpotManagement = () => {
  const { user } = useAuth();
  const [spots, setSpots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [activeTab, setActiveTab] = useState('genel');
  
  // Form state with all fields
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    latitude: '',
    longitude: '',
    difficulty: 'Başlangıç',
    parkingInfo: 'Ücretsiz',
    entryFee: '',
    imageUrl: '',
    waterCondition: 'Sakin',
    bestTime: '',
    accessibility: '',
    facilities: [],
    galleryUrls: [''],
    hazards: ['']
  });

  // File upload states
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState('');
  const [galleryFiles, setGalleryFiles] = useState([null, null, null]); // Fixed 3 slots
  const [galleryPreviews, setGalleryPreviews] = useState(['', '', '']); // Fixed 3 slots
  const [isUploading, setIsUploading] = useState(false);

  const facilityOptions = [
    'Duş',
    'WC',
    'Soyunma Kabini',
    'Otopark',
    'Kafe',
    'Ekipman Kiralama',
    'Cankurtaran'
  ];

  useEffect(() => {
    loadSpots();
  }, []);

  const loadSpots = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await spotService?.getAllSpots();
      setSpots(data || []);
    } catch (err) {
      console.error('Error loading spots:', err);
      setError(err?.message || 'Spotlar yüklenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (spot = null) => {
    // 🔥 DEBUG: Log incoming spot data to verify all fields are present
    console.log("🔥 EDIT MODE - INCOMING SPOT DATA:", spot);
    
    if (spot) {
      setEditingSpot(spot);
      
      // ROBUST DATA MAPPING (Snake_case -> CamelCase)
      // 1. Extract Gallery (Try all possible names)
      const rawGallery = spot?.gallery_urls || spot?.galleryUrls || spot?.gallery || [];
      
      // 2. Extract Main Image
      const rawImage = spot?.image_url || spot?.imageUrl || spot?.image || '';
      
      setFormData({
        name: spot?.name || '',
        description: spot?.description || '',
        location: spot?.location || '',
        latitude: spot?.latitude?.toString() || '',
        longitude: spot?.longitude?.toString() || '',
        
        // FIXED MAPPINGS: Prioritize snake_case DB fields
        difficulty: spot?.difficulty_level || spot?.difficulty || 'Başlangıç',
        parkingInfo: spot?.parking_info || spot?.parkingInfo || 'Ücretsiz',
        entryFee: spot?.entry_fee || spot?.entryFee || '',
        waterCondition: spot?.water_condition || spot?.waterCondition || 'Sakin', // Fixes "Su Durumu"
        bestTime: spot?.best_time || spot?.bestTime || '', // Fixes "En İyi Zaman"
        accessibility: spot?.accessibility || '',
        
        // Arrays (Ensure they are arrays):
        facilities: Array.isArray(spot?.facilities) ? spot?.facilities : [],
        hazards: Array.isArray(spot?.hazards) ? spot?.hazards : [''],
        galleryUrls: rawGallery?.length > 0 ? rawGallery : [''],
        imageUrl: rawImage
      });
      
      // 3. Set Previews
      setMainImagePreview(rawImage);
      setGalleryPreviews([
        rawGallery?.[0] || '',
        rawGallery?.[1] || '',
        rawGallery?.[2] || ''
      ]);
      
      // Reset file inputs
      setGalleryFiles([null, null, null]);
      setMainImageFile(null);
    } else {
      setEditingSpot(null);
      setFormData({
        name: '',
        description: '',
        location: '',
        latitude: '',
        longitude: '',
        difficulty: 'Başlangıç',
        parkingInfo: 'Ücretsiz',
        entryFee: '',
        imageUrl: '',
        waterCondition: 'Sakin',
        bestTime: '',
        accessibility: '',
        facilities: [],
        galleryUrls: [''],
        hazards: ['']
      });
      setMainImagePreview('');
      setGalleryPreviews(['', '', '']);
      setGalleryFiles([null, null, null]);
    }
    setMainImageFile(null);
    setActiveTab('genel');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSpot(null);
    setActiveTab('genel');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFacilityToggle = (facility) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev?.facilities?.includes(facility)
        ? prev?.facilities?.filter(f => f !== facility)
        : [...prev?.facilities, facility]
    }));
  };

  const handleAddGalleryUrl = () => {
    setFormData(prev => ({
      ...prev,
      galleryUrls: [...prev?.galleryUrls, '']
    }));
  };

  const handleRemoveGalleryUrl = (index) => {
    setFormData(prev => ({
      ...prev,
      galleryUrls: prev?.galleryUrls?.filter((_, i) => i !== index)
    }));
  };

  const handleGalleryUrlChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      galleryUrls: prev?.galleryUrls?.map((url, i) => i === index ? value : url)
    }));
  };

  // Handle main image file selection
  const handleMainImageChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setMainImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMainImagePreview(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  // Handle gallery image file selection for specific slot
  const handleGalleryImageChange = (slotIndex, e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file?.size > 10 * 1024 * 1024) {
        setError('Dosya boyutu 10MB\'den büyük olamaz');
        return;
      }
      
      // Update files array
      const newFiles = [...galleryFiles];
      newFiles[slotIndex] = file;
      setGalleryFiles(newFiles);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const newPreviews = [...galleryPreviews];
        newPreviews[slotIndex] = reader?.result;
        setGalleryPreviews(newPreviews);
      };
      reader?.readAsDataURL(file);
    }
  };

  // Remove gallery image from specific slot
  const handleRemoveGalleryImage = (slotIndex) => {
    const newFiles = [...galleryFiles];
    newFiles[slotIndex] = null;
    setGalleryFiles(newFiles);
    
    const newPreviews = [...galleryPreviews];
    newPreviews[slotIndex] = '';
    setGalleryPreviews(newPreviews);
  };

  const handleAddHazard = () => {
    setFormData(prev => ({
      ...prev,
      hazards: [...prev?.hazards, '']
    }));
  };

  const handleRemoveHazard = (index) => {
    setFormData(prev => ({
      ...prev,
      hazards: prev?.hazards?.filter((_, i) => i !== index)
    }));
  };

  const handleHazardChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      hazards: prev?.hazards?.map((hazard, i) => i === index ? value : hazard)
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    try {
      setError('');
      setIsUploading(true);
      
      // Upload main image if new file selected
      let mainImageUrl = formData?.imageUrl;
      if (mainImageFile) {
        console.log('Uploading main image...', mainImageFile);
        try {
          const uploadedUrl = await storageService?.uploadSpotImage(mainImageFile, user?.id);
          console.log('Main image upload result:', uploadedUrl);
          
          if (!uploadedUrl || uploadedUrl === '') {
            throw new Error('Kapak fotoğrafı yüklenemedi. Lütfen dosya formatını kontrol edin ve tekrar deneyin.');
          }
          
          mainImageUrl = uploadedUrl;
          console.log('Main image URL set to:', mainImageUrl);
        } catch (uploadError) {
          console.error('Main image upload error:', uploadError);
          throw new Error(`Kapak fotoğrafı yüklenirken hata oluştu: ${uploadError?.message || 'Bilinmeyen hata'}`);
        }
      }

      // CRITICAL FIX: Upload gallery images - properly preserve existing URLs
      let galleryUrls = ['', '', ''];
      
      // Get the original gallery URLs from the editing spot
      const originalGallery = editingSpot?.galleryUrls || editingSpot?.gallery || [];
      
      // Process each slot (0, 1, 2)
      for (let i = 0; i < 3; i++) {
        if (galleryFiles?.[i]) {
          // New file uploaded for this slot - upload it
          console.log(`Uploading gallery image ${i}...`);
          const uploadedUrl = await storageService?.uploadSpotImage(galleryFiles?.[i], user?.id);
          if (uploadedUrl) {
            galleryUrls[i] = uploadedUrl;
            console.log(`Gallery image ${i} uploaded:`, uploadedUrl);
          }
        } else if (galleryPreviews?.[i] && galleryPreviews?.[i] !== '') {
          // No new file, but preview exists - preserve the existing URL
          // This is the CRITICAL fix: when editing, keep existing photos that weren't replaced
          galleryUrls[i] = originalGallery?.[i] || galleryPreviews?.[i];
          console.log(`Gallery image ${i} preserved:`, galleryUrls?.[i]);
        }
        // else: slot is empty, leave as empty string
      }
      
      // CRITICAL FIX: If no main image but gallery slot 0 exists, use it as main image
      // This handles the case where user uploads to gallery section instead of main image section
      if ((!mainImageUrl || mainImageUrl === '') && galleryUrls?.[0]) {
        console.log('Using gallery slot 0 as main image:', galleryUrls?.[0]);
        mainImageUrl = galleryUrls?.[0];
      }
      
      // Validate that we have a main image URL for new spots
      if (!editingSpot && (!mainImageUrl || mainImageUrl === '')) {
        console.error('Validation failed: No main image URL', { mainImageUrl, mainImageFile, galleryFiles, galleryUrls, editingSpot });
        throw new Error('Lütfen en az bir kapak fotoğrafı yükleyin (Ana Görsel veya Galeri Slot 1).');
      }
      
      console.log('Main image validation passed:', mainImageUrl);
      
      // Filter out empty slots for final save - STRICT 3 PHOTO LIMIT
      const cleanedGalleryUrls = galleryUrls?.filter(url => url?.trim() !== '')?.slice(0, 3);
      
      // Filter out empty hazards
      const cleanedData = {
        ...formData,
        imageUrl: mainImageUrl,
        galleryUrls: cleanedGalleryUrls,
        hazards: formData?.hazards?.filter(h => h?.trim() !== '')
      };
      
      console.log('Saving spot with data:', cleanedData);

      if (editingSpot) {
        await spotService?.updateSpot(editingSpot?.id, cleanedData);
      } else {
        await spotService?.createSpot(cleanedData);
      }

      await loadSpots();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving spot:', err);
      setError(err?.message || 'Spot kaydedilirken bir hata oluştu');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (spotId) => {
    if (!window.confirm('Bu spotu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setError('');
      await spotService?.deleteSpot(spotId);
      await loadSpots();
    } catch (err) {
      console.error('Error deleting spot:', err);
      setError(err?.message || 'Spot silinirken bir hata oluştu');
    }
  };

  const modalTabs = [
    { id: 'genel', label: 'Genel Bilgiler' },
    { id: 'detaylar', label: 'Detaylar' },
    { id: 'imkanlar', label: 'İmkanlar' },
    { id: 'gorseller', label: 'Görseller' },
    { id: 'uyarilar', label: 'Uyarılar' }
  ];

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Spot Yönetimi</h2>
          <p className="text-muted-foreground">SUP spotlarını yönetin</p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => handleOpenModal()}>
          Yeni Spot Ekle
        </Button>
      </div>
      {/* Error Display */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <p className="text-destructive font-medium">{error}</p>
        </div>
      )}
      {/* Spots Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Görsel</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">İsim</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Konum</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Zorluk</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {spots?.map((spot) => (
                <tr key={spot?.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-6 py-4">
                    <img
                      src={spot?.imageUrl || '/assets/images/no_image.png'}
                      alt={spot?.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-medium text-foreground">{spot?.name}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-muted-foreground">{spot?.location}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      spot?.difficulty === 'Başlangıç' ? 'bg-success/10 text-success' :
                      spot?.difficulty === 'Orta'? 'bg-warning/10 text-warning' : 'bg-destructive/10 text-destructive'
                    }`}>
                      {spot?.difficulty}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => handleOpenModal(spot)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                      <Icon name="Edit" size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(spot?.id)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors">
                      <Icon name="Trash2" size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-xl font-bold text-foreground">
                {editingSpot ? 'Spot Düzenle' : 'Yeni Spot Ekle'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors">
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 px-6 pt-4 border-b border-border overflow-x-auto">
              {modalTabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}>
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* Modal Content */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Tab 1: Genel Bilgiler */}
              {activeTab === 'genel' && (
                <div className="space-y-4">
                  <Input
                    label="Spot İsmi *"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    required
                    placeholder="Örn: Caddebostan Sahili"
                  />

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Açıklama *
                    </label>
                    <textarea
                      value={formData?.description}
                      onChange={(e) => handleInputChange('description', e?.target?.value)}
                      required
                      rows={4}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                      placeholder="Spot hakkında detaylı bilgi..."
                    />
                  </div>

                  <Input
                    label="Konum *"
                    value={formData?.location}
                    onChange={(e) => handleInputChange('location', e?.target?.value)}
                    required
                    placeholder="Örn: Kadıköy, İstanbul"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Enlem (Latitude) *"
                      type="number"
                      step="any"
                      value={formData?.latitude}
                      onChange={(e) => handleInputChange('latitude', e?.target?.value)}
                      required
                      placeholder="40.9632"
                    />
                    <Input
                      label="Boylam (Longitude) *"
                      type="number"
                      step="any"
                      value={formData?.longitude}
                      onChange={(e) => handleInputChange('longitude', e?.target?.value)}
                      required
                      placeholder="29.0637"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Zorluk Seviyesi *
                    </label>
                    <select
                      value={formData?.difficulty}
                      onChange={(e) => handleInputChange('difficulty', e?.target?.value)}
                      required
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground">
                      <option value="Başlangıç">Başlangıç</option>
                      <option value="Orta">Orta</option>
                      <option value="İleri">İleri</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Tab 2: Detaylar */}
              {activeTab === 'detaylar' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Otopark Bilgisi
                    </label>
                    <select
                      value={formData?.parkingInfo}
                      onChange={(e) => handleInputChange('parkingInfo', e?.target?.value)}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground">
                      <option value="Ücretsiz">Ücretsiz</option>
                      <option value="Ücretli">Ücretli</option>
                    </select>
                  </div>

                  <Input
                    label="Giriş Ücreti"
                    value={formData?.entryFee}
                    onChange={(e) => handleInputChange('entryFee', e?.target?.value)}
                    placeholder="Örn: Ücretsiz, 50 TL"
                  />

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Su Durumu
                    </label>
                    <select
                      value={formData?.waterCondition}
                      onChange={(e) => handleInputChange('waterCondition', e?.target?.value)}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground">
                      <option value="Sakin">Sakin</option>
                      <option value="Dalgalı">Dalgalı</option>
                    </select>
                  </div>

                  <Input
                    label="En İyi Zaman"
                    value={formData?.bestTime}
                    onChange={(e) => handleInputChange('bestTime', e?.target?.value)}
                    placeholder="Örn: Mayıs - Eylül"
                  />

                  <Input
                    label="Erişilebilirlik"
                    value={formData?.accessibility}
                    onChange={(e) => handleInputChange('accessibility', e?.target?.value)}
                    placeholder="Örn: Toplu taşıma ile kolay ulaşım"
                  />
                </div>
              )}

              {/* Tab 3: İmkanlar */}
              {activeTab === 'imkanlar' && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground mb-4">
                    Spotun sunduğu imkanları seçin:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {facilityOptions?.map((facility) => (
                      <label
                        key={facility}
                        className="flex items-center gap-2 p-3 rounded-lg border border-border hover:bg-muted/30 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={formData?.facilities?.includes(facility)}
                          onChange={() => handleFacilityToggle(facility)}
                          className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
                        />
                        <span className="text-sm font-medium text-foreground">{facility}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 4: Görseller */}
              {activeTab === 'gorseller' && (
                <div className="space-y-6">
                  {/* Main Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Ana Görsel *
                    </label>
                    
                    {/* Image Preview */}
                    {mainImagePreview && (
                      <div className="mb-3 relative inline-block">
                        <img
                          src={mainImagePreview}
                          alt="Ana görsel önizleme"
                          className="w-full max-w-md h-48 object-cover rounded-lg border-2 border-border"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setMainImageFile(null);
                            setMainImagePreview('');
                            setFormData(prev => ({ ...prev, imageUrl: '' }));
                          }}
                          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-destructive text-white hover:bg-destructive/90 transition-colors flex items-center justify-center">
                          <Icon name="X" size={16} />
                        </button>
                      </div>
                    )}

                    {/* File Input */}
                    <div className="flex items-center gap-3">
                      <label className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors">
                          <Icon name="Upload" size={20} className="text-muted-foreground" />
                          <span className="text-sm font-medium text-muted-foreground">
                            {mainImageFile ? mainImageFile?.name : 'Görsel Seç veya Sürükle'}
                          </span>
                        </div>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleMainImageChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Maksimum dosya boyutu: 10MB. Desteklenen formatlar: JPG, PNG, WebP
                    </p>
                  </div>

                  {/* Gallery Images - Fixed 3 Slot Grid Layout */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">
                      Galeri Görselleri (Maksimum 3 Fotoğraf)
                    </label>
                    
                    {/* Fixed 3-Slot Grid: 1 Big Left + 2 Small Right */}
                    <div className="grid grid-cols-3 gap-4 h-[400px]">
                      {/* Slot 1: Cover Photo (Left - 2 columns) */}
                      <div className="col-span-2 h-full">
                        {galleryPreviews?.[0] ? (
                          <div className="relative w-full h-full group">
                            <img
                              src={galleryPreviews?.[0]}
                              alt="Kapak Fotoğrafı"
                              className="w-full h-full object-cover rounded-xl border-2 border-border"
                            />
                            {/* Hover Overlay with Delete Button */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                              <button
                                type="button"
                                onClick={() => handleRemoveGalleryImage(0)}
                                className="px-4 py-2 bg-destructive text-white rounded-lg hover:bg-destructive/90 transition-colors flex items-center gap-2">
                                <Icon name="Trash2" size={18} />
                                <span className="text-sm font-medium">Sil</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="w-full h-full cursor-pointer">
                            <div className="w-full h-full border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center justify-center gap-3">
                              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                                <Icon name="Plus" size={32} className="text-primary" />
                              </div>
                              <div className="text-center">
                                <p className="text-sm font-semibold text-foreground">Kapak Fotoğrafı</p>
                                <p className="text-xs text-muted-foreground mt-1">Tıklayarak yükleyin</p>
                              </div>
                            </div>
                            <input
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={(e) => handleGalleryImageChange(0, e)}
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>

                      {/* Right Column: Slot 2 & 3 (Stacked) */}
                      <div className="flex flex-col gap-4 h-full">
                        {/* Slot 2: Detail Photo 1 */}
                        <div className="flex-1">
                          {galleryPreviews?.[1] ? (
                            <div className="relative w-full h-full group">
                              <img
                                src={galleryPreviews?.[1]}
                                alt="Detay Fotoğrafı 1"
                                className="w-full h-full object-cover rounded-xl border-2 border-border"
                              />
                              {/* Hover Overlay with Delete Button */}
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveGalleryImage(1)}
                                  className="px-3 py-2 bg-destructive text-white rounded-lg hover:bg-destructive/90 transition-colors flex items-center gap-2">
                                  <Icon name="Trash2" size={16} />
                                  <span className="text-xs font-medium">Sil</span>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="w-full h-full cursor-pointer">
                              <div className="w-full h-full border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center justify-center gap-2">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                  <Icon name="Plus" size={24} className="text-primary" />
                                </div>
                                <div className="text-center">
                                  <p className="text-xs font-semibold text-foreground">Detay 1</p>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">Tıklayın</p>
                                </div>
                              </div>
                              <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                onChange={(e) => handleGalleryImageChange(1, e)}
                                className="hidden"
                              />
                            </label>
                          )}
                        </div>

                        {/* Slot 3: Detail Photo 2 */}
                        <div className="flex-1">
                          {galleryPreviews?.[2] ? (
                            <div className="relative w-full h-full group">
                              <img
                                src={galleryPreviews?.[2]}
                                alt="Detay Fotoğrafı 2"
                                className="w-full h-full object-cover rounded-xl border-2 border-border"
                              />
                              {/* Hover Overlay with Delete Button */}
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveGalleryImage(2)}
                                  className="px-3 py-2 bg-destructive text-white rounded-lg hover:bg-destructive/90 transition-colors flex items-center gap-2">
                                  <Icon name="Trash2" size={16} />
                                  <span className="text-xs font-medium">Sil</span>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="w-full h-full cursor-pointer">
                              <div className="w-full h-full border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center justify-center gap-2">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                  <Icon name="Plus" size={24} className="text-primary" />
                                </div>
                                <div className="text-center">
                                  <p className="text-xs font-semibold text-foreground">Detay 2</p>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">Tıklayın</p>
                                </div>
                              </div>
                              <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                onChange={(e) => handleGalleryImageChange(2, e)}
                                className="hidden"
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-2">
                      Maksimum 3 fotoğraf yükleyebilirsiniz. İlk fotoğraf kapak görseli olarak kullanılır.
                    </p>
                    <p className="text-xs text-orange-600 dark:text-orange-400 font-medium mt-1">
                      ⚠️ GLOBAL SINIR: Spot başına maksimum 3 fotoğraf yüklenebilir.
                    </p>
                  </div>
                </div>
              )}

              {/* Tab 5: Uyarılar */}
              {activeTab === 'uyarilar' && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground mb-4">
                    Spotta dikkat edilmesi gereken tehlikeler veya uyarılar:
                  </p>
                  <div className="space-y-2">
                    {formData?.hazards?.map((hazard, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={hazard}
                          onChange={(e) => handleHazardChange(index, e?.target?.value)}
                          placeholder="Örn: Rüzgarlı havada dalgalı"
                          className="flex-1 px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveHazard(index)}
                          className="w-10 h-10 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors flex items-center justify-center">
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddHazard}
                      className="w-full px-4 py-2 border-2 border-dashed border-border rounded-lg text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2">
                      <Icon name="Plus" size={16} />
                      <span>Uyarı Ekle</span>
                    </button>
                  </div>
                </div>
              )}
            </form>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseModal}
                disabled={isUploading}>
                İptal
              </Button>
              <Button
                type="submit"
                variant="default"
                onClick={handleSubmit}
                disabled={isUploading}>
                {isUploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2"></div>
                    Yükleniyor...
                  </>
                ) : (
                  editingSpot ? 'Güncelle' : 'Oluştur'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpotManagement;